import {parseImportoLocale} from '../../utilities/funzioni-stringhe';

export interface AmountParseResult {
  value: number;
  consumedRaw: string;
}

export interface ExtractAmountResult {
  amountResult: AmountParseResult;
  textWithPlaceholder: string;
}

/**
 * Numero con separatori locale-aware. Due rami (il primo ha priorità):
 *  1. gruppi di migliaia: `1.500`, `12.345,67`, `1.234.567,89` — uno o più gruppi da 3 cifre
 *     separati da `.` (o `,`), con decimale opzionale a 1-2 cifre;
 *  2. numero semplice: `50`, `30,50`, `30.50` — un `.`/`,` seguito da 1-2 cifre è decimale.
 * Il parsing effettivo (quale separatore è decimale) è delegato a `parseImportoLocale`.
 */
const NUMERO = '(\\d{1,3}(?:\\.\\d{3})+(?:,\\d{1,2})?|\\d{1,3}(?:,\\d{3})+(?:\\.\\d{1,2})?|\\d+(?:[.,]\\d{1,2})?)';

/**
 * Regex patterns for amount extraction. Each requires an explicit currency signal (€ or "euro").
 * Gestisce il separatore delle migliaia IT/EN (vedi `NUMERO`).
 *
 * Patterns are tested in order; first match wins.
 */
const AMOUNT_PATTERNS: RegExp[] = [
  // €50 / € 50 / €30,50 / €1.500 / €12.345,67
  new RegExp(`€\\s*${NUMERO}`),
  // 50€ / 30,50€ / 1.500€
  new RegExp(`${NUMERO}\\s*€`),
  // 50 euro / 30,50 euro / 1.500 euro
  new RegExp(`${NUMERO}\\s+euro\\b`),
];

function parseAmount(raw: string): number {
  return parseImportoLocale(raw);
}

/**
 * Extracts an amount from text that has already been processed by extractDates
 * (date segments replaced with __DATE__). Requires explicit currency signal.
 *
 * Returns null if no amount found or amount is invalid (<= 0).
 */
export function extractAmount(text: string): ExtractAmountResult | null {
  const lower = text.toLowerCase();

  for (const pattern of AMOUNT_PATTERNS) {
    const match = lower.match(pattern);
    if (!match) continue;

    const value = parseAmount(match[1]);
    if (!isFinite(value) || value <= 0) continue;

    const matchStart = match.index!;
    const matchEnd = matchStart + match[0].length;

    // Replace matched segment in original text (preserve case outside match)
    const textWithPlaceholder =
      text.substring(0, matchStart).trimEnd() +
      ' __AMOUNT__ ' +
      text.substring(matchEnd).trimStart();

    return {
      amountResult: {
        value,
        consumedRaw: text.substring(matchStart, matchEnd).trim(),
      },
      textWithPlaceholder: textWithPlaceholder.replace(/\s+/g, ' ').trim(),
    };
  }

  return null;
}
